import React, { FC } from 'react';
import { TCourier } from '../../services/api';
import CourierDetailActionsButton from '../CourierDetailActionsButton';
import { EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import { PageDivider } from '@src/shared/components/PageDivider';
import { IconEyeHide, IconEyeShow } from 'snacks-design-system';
import { useTranslation } from 'react-i18next';
import { mergeStrings } from '@src/shared/utils';
import PIESwitch from '@src/shared/components/PIESwitch';
import useFMFeatureFlag from '@src/shared/hooks/use-fm-featureflag';
import { JETFM_ORDER_TOGGLE } from '@src/shared/models';
import {
  trackManageCourierShowHideOrderToggleOff,
  trackManageCourierShowHideOrderToggleOn,
} from '../../tracking/courier-card';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useShowUnassignedOrderMutation } from '../../queries';
import { useQueryClient } from 'react-query';
import PIENotification, { IPieNotificationMode } from '@src/shared/components/PIENotification';
import PIEText from '@src/shared/components/PIEText';
import styles from './CourierData.module.scss';
import useOCFStatus from '@src/shared/hooks/use-ocf-status';
interface ICourierData extends TCourier {
  onAction: (action: TBottomSheetAction) => void;
}

export const CourierData: FC<ICourierData> = ({
  firstName,
  lastName,
  email,
  onAction,
  hash,
  id,
  settings,
}) => {
  const { t } = useTranslation();
  const { restaurant } = useKeycloakUserState();
  const isOrderToggleActive = useFMFeatureFlag(JETFM_ORDER_TOGGLE);
  const queryClient = useQueryClient();
  const showUnassignedOrderMutation = useShowUnassignedOrderMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries('couriers');
    },
    onError: () => {},
  });
  const { isOCFEnabled } = useOCFStatus();
  const handleOnButtonClick = (e: TBottomSheetAction) => {
    if (e.type === EBottomSheetAction.Delete || e.type === EBottomSheetAction.Disconnect) {
      onAction({
        type: e.type,
        source: { hash, id, text: mergeStrings(firstName, lastName) },
      });
    }
  };

  const sendAnalyticsData = (value: Boolean) => {
    const analyticsCallFunction = value
      ? trackManageCourierShowHideOrderToggleOn
      : trackManageCourierShowHideOrderToggleOff;
    analyticsCallFunction(restaurant?.country as any, restaurant?.language as any);
  };

  const handleOnSwitchChange = async (value: Boolean) => {
    if (restaurant?.id) {
      await showUnassignedOrderMutation.mutate({
        courierId: id,
        isEnabled: !!value,
        restaurantId: restaurant?.id,
      });
      sendAnalyticsData(value);
    }
  };

  return (
    <div className={styles.courierDataContainer}>
      <div className={styles.courierDataItemsFirst}>
        <PIEText
          size="var(--xds-font-size-24)"
          fontWeight="var(--xds-font-weight-extrabold)"
          text={mergeStrings(firstName, lastName)}
        />
      </div>
      <div className={styles.courierDataItemsSecond}>
        <PIEText
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
          text={t('manage_couriers.COURIER_DATA_EMAIL')}
        />
        <PIEText
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-regular)"
          text={email}
        />
      </div>
      <PageDivider
        styles={{
          backgroundColor: '#00000014',
          height: '12px',
          flexShrink: '0',
          width: '100%',
        }}
      />
      {isOrderToggleActive ? (
        <>
          <div className={styles.courierDataOrderToggle} id="courier-order-toggle">
            <PIEText
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
              text={t('order_status.TITLE')}
            />
            <PIESwitch
              label={
                settings?.showUnassignedOrders
                  ? t('order_status.TOGGLE_ON')
                  : t('order_status.TOGGLE_OFF')
              }
              labelPosition="right"
              setOnByDefault={!!settings?.showUnassignedOrders}
              onChange={({ isSwitchOn }) => {
                handleOnSwitchChange(!!isSwitchOn);
              }}
            />
            <PIENotification
              isOpen={true}
              showClose={false}
              mode={
                settings?.showUnassignedOrders
                  ? IPieNotificationMode.Positive
                  : IPieNotificationMode.Neutral
              }
              modInline
              onClose={() => {}}
              icon={settings?.showUnassignedOrders ? <IconEyeShow /> : <IconEyeHide />}
            >
              <PIEText
                size={''}
                text={
                  settings?.showUnassignedOrders
                    ? t('order_status.TOGGLE_ON_CONTENT')
                    : t('order_status.TOGGLE_OFF_CONTENT')
                }
              />
            </PIENotification>
          </div>
          <PageDivider
            styles={{
              backgroundColor: '#00000014',
              height: '12px',
              flexShrink: '0',
              width: '100%',
            }}
          />
        </>
      ) : null}
      <div id="courier-data-action-buttons" style={{ width: '100%' }}>
        <div className={styles.courierDataItemsButtonCollection}>
          <CourierDetailActionsButton
            containerCustomStyle={{ paddingRight: 'var(--xds-spacing-b)' }}
            buttonText={t('manage_couriers.REMOVE_BUTTON')}
            onButtonAction={handleOnButtonClick}
            actionType={EBottomSheetAction.Delete}
          />
          {(!isOCFEnabled && (
            <CourierDetailActionsButton
              containerCustomStyle={{ paddingLeft: 'var(--xds-spacing-b)' }}
              buttonText={t('manage_couriers.DISCONNECT_BUTTON')}
              customStyle={{
                paddingLeft: 'var(--xds-spacing-d)',
                paddingRight: 'var(--xds-spacing-d)',
              }}
              onButtonAction={handleOnButtonClick}
              actionType={EBottomSheetAction.Disconnect}
            />
          )) || <></>}
        </div>
      </div>
    </div>
  );
};
